import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Download, Type, Minus, Plus } from "lucide-react";

export default function BookReader() {
  const { bookId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [size, setSize] = useState(17);
  const [serif, setSerif] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/books/${bookId}/text`);
        setData(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Could not load text");
      }
    })();
  }, [bookId]);

  if (err) return (
    <div className="max-w-2xl mx-auto text-center py-16">
      <div className="text-slate-700">{err}</div>
      <Link to="/books" className="mt-4 inline-block text-teal-700 font-semibold">Back to library</Link>
    </div>
  );
  if (!data) return <div className="text-slate-500">Loading book…</div>;

  const download = () => {
    const blob = new Blob([data.text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${data.title || "book"}.txt`;
    a.click(); URL.revokeObjectURL(url);
  };

  const epub = data.formats?.["application/epub+zip"];

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid={`book-reader-${bookId}`}>
      <Link to="/books" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-teal-700"><ArrowLeft size={14} /> All books</Link>

      <header className="flex flex-wrap items-end justify-between gap-3 pb-3 border-b border-slate-200">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900" data-testid="book-title">{data.title}</h1>
          <div className="text-sm text-slate-500">{data.authors?.map((a) => a.name).join(", ")}</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 border border-slate-200 rounded-full px-2 py-1">
            <button data-testid="reader-smaller" onClick={() => setSize((s) => Math.max(13, s - 1))} className="p-1 text-slate-500 hover:text-slate-900"><Minus size={14} /></button>
            <Type size={12} className="text-slate-400" />
            <button data-testid="reader-larger" onClick={() => setSize((s) => Math.min(24, s + 1))} className="p-1 text-slate-500 hover:text-slate-900"><Plus size={14} /></button>
          </div>
          <button data-testid="reader-serif" onClick={() => setSerif((s) => !s)} className="px-3 py-1.5 rounded-full border border-slate-200 text-xs font-semibold">
            {serif ? "Serif" : "Sans"}
          </button>
          <button data-testid="reader-download-txt" onClick={download} className="px-4 py-2 rounded-full bg-teal-700 hover:bg-teal-800 text-white text-sm font-semibold inline-flex items-center gap-1"><Download size={12} /> .txt</button>
          {epub && <a href={epub} target="_blank" rel="noreferrer" data-testid="reader-download-epub"
            className="px-4 py-2 rounded-full border border-slate-200 text-slate-700 text-sm font-semibold inline-flex items-center gap-1"><Download size={12} /> EPUB</a>}
        </div>
      </header>

      <article data-testid="book-text"
        style={{ fontSize: `${size}px`, fontFamily: serif ? '"Georgia", "Iowan Old Style", serif' : '"Figtree", ui-sans-serif' }}
        className="whitespace-pre-wrap leading-[1.75] text-slate-800 max-h-[70vh] overflow-y-auto p-4 rounded-2xl bg-white border border-slate-200">
        {data.text}
      </article>
    </div>
  );
}
